import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

/**
 * Sign in.
 *
 * Accepts either nickname or email, because players remember one or the
 * other. The server returns one generic error for both "wrong password" and
 * "no such user", so this form cannot be used to discover which nicknames
 * exist.
 */
export function Login({ onSignedIn }: { onSignedIn?: () => void }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ login, password }),
      });
      if (!res.ok) {
        setError(
          res.status === 401
            ? "Wrong nickname/email or password"
            : "Could not sign in, please try again"
        );
        return;
      }
      onSignedIn?.();
      navigate("/");
    } catch {
      setError("Could not reach the server");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 400 }}>
      <h2 style={{ marginTop: 0 }}>Sign in</h2>

      <form onSubmit={submit}>
        <div style={{ marginBottom: 14 }}>
          <label style={label}>Nickname or email</label>
          <input
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            style={input}
            autoComplete="username"
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={input}
            autoComplete="current-password"
          />
        </div>

        {error && (
          <div style={{ color: "#c62828", fontSize: 14, marginBottom: 12 }}>{error}</div>
        )}

        <button type="submit" disabled={busy} style={submitBtn(busy)}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p style={{ fontSize: 14, color: "#666", marginTop: 20 }}>
        No account yet? <Link to="/register" style={link}>Create one</Link>.
      </p>
    </div>
  );
}

const label: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
};

const input: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 14,
  border: "1px solid #ccc",
  borderRadius: 6,
  boxSizing: "border-box",
};

const link: React.CSSProperties = { color: "#00695c", fontWeight: 600 };

const submitBtn = (busy: boolean): React.CSSProperties => ({
  padding: "10px 20px",
  fontSize: 15,
  fontWeight: 600,
  color: "#fff",
  background: busy ? "#90a4ae" : "#00695c",
  border: "none",
  borderRadius: 6,
  cursor: busy ? "default" : "pointer",
});
